/**
 * F3 (paridad Ledgeria) — Tesorería.
 *
 * Tres cosas, en el orden en que se usan: dar de alta la cuenta, soltar el
 * fichero del banco, y bajar la lista de pendientes conciliando. El movimiento
 * bancario no se edita nunca — es un hecho externo; lo único que se decide aquí
 * es contra qué factura o gasto va.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  Ban,
  Check,
  Landmark,
  Link2,
  Loader2,
  Plus,
  RotateCcw,
  Upload,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import { PENDING_KEY } from './BankingCallback'
import { currentLocaleTag } from '../lib/formatting'
import { formatCents } from '../lib/money'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import type {
  BankAccountDto,
  PartnerAdvanceDto,
  PartnerAdvancesResponse,
  BankAccountListResponse,
  BankTransactionDto,
  BankTransactionListResponse,
  BankTransactionStatusT,
  ImportStatementResponse,
  MatchCandidateDto,
  MatchCandidatesResponse,
} from '@toga/shared'

const STATUS_PILL: Record<BankTransactionStatusT, string> = {
  PENDIENTE: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  CONCILIADO: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  IGNORADO: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
}

const inputCls = 'rounded-md border px-2 py-1.5 text-sm'
const inputStyle = { background: 'var(--bg-input)', borderColor: 'var(--border)' } as const

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | undefined)?.error ?? err.message
  }
  return err instanceof Error ? err.message : fallback
}

export default function Banking() {
  const { t } = useTranslation(['banking', 'common'])
  const auth = useAuth()
  const canWrite = auth.status === 'authenticated' && auth.user.role === 'ADMIN'

  const [accounts, setAccounts] = useState<BankAccountDto[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [status, setStatus] = useState<'' | BankTransactionStatusT>('PENDIENTE')
  const [query, setQuery] = useState('')
  const [data, setData] = useState<BankTransactionListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [importResult, setImportResult] = useState<ImportStatementResponse | null>(null)
  const [reconciling, setReconciling] = useState<BankTransactionDto | null>(null)
  // Detalle del último fallo del banco, para no tener que entrar al servidor.
  const [feedDetail, setFeedDetail] = useState<BankAccountDto | null>(null)

  const loadAccounts = useCallback(async () => {
    try {
      const r = await api.get<BankAccountListResponse>('/banking/accounts')
      setAccounts(r.items)
      setSelectedId((prev) => prev ?? r.items[0]?.id ?? null)
    } catch (err) {
      setError(errorText(err, t('errors.loadAccounts')))
    }
  }, [t])

  const loadTransactions = useCallback(async () => {
    if (!selectedId) {
      setData(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ bankAccountId: selectedId, pageSize: '100' })
      if (status) params.set('status', status)
      if (query.trim()) params.set('q', query.trim())
      setData(await api.get<BankTransactionListResponse>(`/banking/transactions?${params}`))
    } catch (err) {
      setError(errorText(err, t('errors.loadTransactions')))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [selectedId, status, query, t])

  useEffect(() => {
    void loadAccounts()
  }, [loadAccounts])
  useEffect(() => {
    void loadTransactions()
  }, [loadTransactions])

  const selected = useMemo(
    () => accounts.find((a) => a.id === selectedId) ?? null,
    [accounts, selectedId]
  )

  const refreshAll = async () => {
    await Promise.all([loadAccounts(), loadTransactions()])
  }

  const onIgnore = async (tx: BankTransactionDto, undo: boolean) => {
    try {
      await api.post(`/banking/transactions/${tx.id}/${undo ? 'unignore' : 'ignore'}`, {})
      await refreshAll()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Landmark size={20} className="text-sky-600 dark:text-sky-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">{t('subtitle')}</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            {selected && <ConnectBankButton account={selected} onError={setError} />}
            {selected && (
              <ImportButton
                accountId={selected.id}
                onImported={async (result) => {
                  setImportResult(result)
                  await refreshAll()
                }}
                onError={setError}
              />
            )}
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-sm"
            >
              <Plus size={15} /> {t('newAccount')}
            </button>
          </div>
        )}
      </header>

      {error && (
        <div className="surface p-3 flex items-start gap-2 border-rose-500/40">
          <AlertCircle size={16} className="text-rose-400 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
        </div>
      )}

      {accounts.length === 0 ? (
        <section className="surface p-6 text-center">
          <p className="text-sm t-muted">{t('noAccounts')}</p>
        </section>
      ) : (
        <>
          {/* Cuentas: el saldo del banco frente a lo que queda por conciliar. */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {accounts.map((a) => {
              const active = a.id === selectedId
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedId(a.id)}
                  className={[
                    'surface p-3 text-left transition',
                    active ? 'ring-2 ring-toga-500/50' : 'hover:bg-hover',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold t-primary text-sm truncate">{a.name}</span>
                    {a.pendingCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40 shrink-0">
                        {t('pendingBadge', { count: a.pendingCount })}
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[11px] t-faint mt-0.5">{a.iban}</div>
                  <BalanceBlock account={a} />
                  <ConsentNotice account={a} />
                  {a.feedLastError && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation()
                        setFeedDetail(a)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation()
                          setFeedDetail(a)
                        }
                      }}
                      className="mt-2 flex items-center gap-1.5 text-[10px] rounded-md border px-1.5 py-1 bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40 hover:bg-rose-500/25 cursor-pointer"
                    >
                      <AlertCircle size={12} className="shrink-0" />
                      <span className="truncate">{t('feed.lastErrorShort')}</span>
                    </div>
                  )}
                </button>
              )
            })}
          </section>

          {/* Movimientos */}
          <section className="surface">
            <div className="p-3 flex flex-wrap items-center gap-2 justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as '' | BankTransactionStatusT)}
                  className={inputCls}
                  style={inputStyle}
                >
                  <option value="PENDIENTE">{t('filters.pending')}</option>
                  <option value="CONCILIADO">{t('filters.reconciled')}</option>
                  <option value="IGNORADO">{t('filters.ignored')}</option>
                  <option value="">{t('filters.all')}</option>
                </select>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('filters.searchPlaceholder')}
                  className={`${inputCls} w-56`}
                  style={inputStyle}
                />
              </div>
              {data && (
                <div className="flex items-center gap-4 text-xs">
                  <span className="t-muted">
                    {t('totals.in')}{' '}
                    <span className="font-mono t-primary">{formatCents(data.totals.inCents)}</span>
                  </span>
                  <span className="t-muted">
                    {t('totals.out')}{' '}
                    <span className="font-mono t-primary">{formatCents(data.totals.outCents)}</span>
                  </span>
                  <span className="t-muted">
                    {t('totals.pending')}{' '}
                    <span className="font-mono t-primary">{data.totals.pendingCount}</span>
                  </span>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs t-muted uppercase tracking-wider" style={{ borderBottom: '1px solid var(--border)' }}>
                    <th className="px-3 py-2">{t('columns.date')}</th>
                    <th className="px-3 py-2">{t('columns.concept')}</th>
                    <th className="px-3 py-2 text-right">{t('columns.amount')}</th>
                    <th className="px-3 py-2 text-right">{t('columns.balance')}</th>
                    <th className="px-3 py-2">{t('columns.status')}</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-xs t-muted">
                        <Loader2 size={14} className="animate-spin inline mr-1.5" />
                        {t('loading')}
                      </td>
                    </tr>
                  ) : (data?.items.length ?? 0) === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-xs t-muted">
                        {t('empty')}
                      </td>
                    </tr>
                  ) : (
                    data!.items.map((tx) => (
                      <tr key={tx.id} style={{ borderBottom: '1px solid var(--border)' }} className="t-primary">
                        <td className="px-3 py-1.5 text-xs whitespace-nowrap tabular-nums">
                          {new Date(tx.operationDate).toLocaleDateString(currentLocaleTag())}
                        </td>
                        <td className="px-3 py-1.5 max-w-md">
                          <div className="truncate" title={tx.concept}>
                            {tx.concept}
                          </div>
                          {tx.matchedLabel && (
                            <div className="text-[10px] t-faint flex items-center gap-1">
                              <Link2 size={9} /> {tx.matchedLabel}
                            </div>
                          )}
                          {/* No hay estado «parcial»: un movimiento aplicado a
                              medias se queda en PENDIENTE con su importe
                              entero, igual que uno sin tocar. Sin esto, quien
                              concilia una factura por menos del importe no se
                              entera de que ha dejado un resto vivo. */}
                          {tx.appliedCents > 0 &&
                            tx.appliedCents < Math.abs(tx.amountCents) && (
                              <div className="text-[10px] text-amber-700 dark:text-amber-300 font-mono">
                                {t('reconcile.remaining', {
                                  amount: formatCents(
                                    Math.abs(tx.amountCents) - tx.appliedCents
                                  ),
                                })}
                              </div>
                            )}
                        </td>
                        <td
                          className={[
                            'px-3 py-1.5 text-right font-mono text-xs whitespace-nowrap',
                            tx.amountCents < 0
                              ? 'text-rose-700 dark:text-rose-300'
                              : 'text-emerald-700 dark:text-emerald-300',
                          ].join(' ')}
                        >
                          {formatCents(tx.amountCents)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs t-muted whitespace-nowrap">
                          {tx.balanceCents != null ? formatCents(tx.balanceCents) : ''}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_PILL[tx.status]}`}>
                            {t(`status.${tx.status}`)}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">
                          {canWrite && tx.status === 'PENDIENTE' && (
                            <>
                              <button
                                type="button"
                                onClick={() => setReconciling(tx)}
                                className="px-2 py-1 rounded text-xs text-toga-700 dark:text-toga-300 hover:bg-hover inline-flex items-center gap-1"
                              >
                                <Link2 size={12} /> {t('actions.reconcile')}
                              </button>
                              <button
                                type="button"
                                onClick={() => void onIgnore(tx, false)}
                                title={t('actions.ignore')}
                                className="p-1 rounded hover:bg-hover t-muted"
                              >
                                <Ban size={12} />
                              </button>
                            </>
                          )}
                          {canWrite && tx.status === 'IGNORADO' && (
                            <button
                              type="button"
                              onClick={() => void onIgnore(tx, true)}
                              title={t('actions.unignore')}
                              className="p-1 rounded hover:bg-hover t-muted"
                            >
                              <RotateCcw size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {creating && (
        <NewAccountModal
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await loadAccounts()
          }}
        />
      )}

      {importResult && (
        <ImportResultModal result={importResult} onClose={() => setImportResult(null)} />
      )}

      {feedDetail && (
        <FeedErrorModal account={feedDetail} onClose={() => setFeedDetail(null)} />
      )}

      {reconciling && (
        <ReconcileModal
          transaction={reconciling}
          onClose={() => setReconciling(null)}
          onRefresh={refreshAll}
          onDone={async () => {
            setReconciling(null)
            await refreshAll()
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function ImportButton({
  accountId,
  onImported,
  onError,
}: {
  accountId: string
  onImported: (r: ImportStatementResponse) => Promise<void> | void
  onError: (msg: string) => void
}) {
  const { t } = useTranslation('banking')
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const r = await api.upload<ImportStatementResponse>(
        `/banking/accounts/${accountId}/import`,
        form
      )
      await onImported(r)
    } catch (err) {
      onError(errorText(err, t('errors.import')))
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-base text-sm cursor-pointer hover:bg-hover t-primary">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
      {t('importStatement')}
      <input ref={inputRef} type="file" className="hidden" onChange={onPick} disabled={busy} />
    </label>
  )
}

function ImportResultModal({
  result,
  onClose,
}: {
  result: ImportStatementResponse
  onClose: () => void
}) {
  const { t } = useTranslation(['banking', 'common'])
  const { statement, warnings, balanceCheck } = result
  const balanced = balanceCheck.diffCents === 0

  return (
    <Modal open onClose={onClose} title={t('import.title')} maxWidth="max-w-lg">
      <div className="space-y-3 text-sm">
        <p className="t-primary">
          {t('import.summary', {
            imported: statement.importedCount,
            duplicates: statement.duplicateCount,
          })}
        </p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Meta label={t('import.period')} value={`${statement.fromDate.slice(0, 10)} → ${statement.toDate.slice(0, 10)}`} />
          <Meta label={t('import.closingBalance')} value={formatCents(statement.closingBalanceCents)} />
        </div>

        {/* Lo importante del import no es cuántas filas entraron, sino si el
            banco y la contabilidad dicen lo mismo. */}
        <div
          className={[
            'rounded-lg border p-3 text-xs',
            balanced
              ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
              : 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300',
          ].join(' ')}
        >
          <p className="font-medium flex items-center gap-1.5">
            {balanced ? <Check size={13} /> : <AlertCircle size={13} />}
            {balanced ? t('import.balanceOk') : t('import.balanceDiff')}
          </p>
          {!balanced && (
            <p className="mt-1">
              {t('import.balanceDetail', {
                bank: formatCents(balanceCheck.bankCents),
                ledger: formatCents(balanceCheck.ledgerCents),
                diff: formatCents(balanceCheck.diffCents),
              })}
            </p>
          )}
        </div>

        {warnings.length > 0 && (
          <ul className="text-xs t-muted list-disc pl-4 space-y-0.5">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider t-muted">{label}</div>
      <div className="t-primary font-mono">{value}</div>
    </div>
  )
}

function NewAccountModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => Promise<void> | void
}) {
  const { t } = useTranslation(['banking', 'common'])
  const [name, setName] = useState('')
  const [iban, setIban] = useState('')
  const [accountCode, setAccountCode] = useState('572')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.post('/banking/accounts', { name: name.trim(), iban, accountCode, currency: 'EUR' })
      await onCreated()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('newAccount')} maxWidth="max-w-md">
      <div className="space-y-3">
        <Field label={t('fields.name')}>
          <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} w-full`} style={inputStyle} />
        </Field>
        <Field label={t('fields.iban')}>
          <input
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            placeholder="ES00 0000 0000 0000 0000 0000"
            className={`${inputCls} w-full font-mono`}
            style={inputStyle}
          />
        </Field>
        <Field label={t('fields.accountCode')} hint={t('fields.accountCodeHint')}>
          <input
            value={accountCode}
            onChange={(e) => setAccountCode(e.target.value)}
            className={`${inputCls} w-full font-mono`}
            style={inputStyle}
          />
        </Field>

        {error && (
          <p className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <AlertCircle size={12} /> {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md text-sm t-muted hover:bg-hover">
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving || !name.trim() || !iban.trim()}
            className="px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            {t('common:actions.save')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-xs t-muted block mb-1">{label}</span>
      {children}
      {hint && <span className="text-[10px] t-faint block mt-0.5">{hint}</span>}
    </label>
  )
}

/**
 * Conciliar: el emparejador propone y la persona decide. Se enseña siempre POR
 * QUÉ se propone cada candidato — un match sin explicación se acepta a ciegas.
 */
function ReconcileModal({
  transaction,
  onClose,
  onRefresh,
  onDone,
}: {
  transaction: BankTransactionDto
  onClose: () => void
  onRefresh: () => Promise<void> | void
  onDone: () => Promise<void> | void
}) {
  const { t } = useTranslation(['banking', 'common'])
  const [candidates, setCandidates] = useState<MatchCandidateDto[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * El movimiento VIVO, no el que se abrió.
   *
   * Antes se aplicaba una factura y el modal se cerraba, aunque quedara un
   * sobrante. En la lista el movimiento sigue en PENDIENTE con su importe
   * entero —no hay estado «parcial»—, así que nadie se enteraba de que había
   * quedado a medias hasta que volvía a abrirlo por casualidad. Ahora la
   * ventana se queda abierta mientras quede algo por aplicar y lo dice.
   */
  const [tx, setTx] = useState<BankTransactionDto>(transaction)
  const [notice, setNotice] = useState<string | null>(null)
  /** Se sube tras cada aplicación: los candidatos cambian con lo que queda. */
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    api
      .get<MatchCandidatesResponse>(`/banking/transactions/${tx.id}/candidates`)
      .then((r) => {
        if (!cancelled) setCandidates(r.items)
      })
      .catch((err) => {
        if (!cancelled) setError(errorText(err, t('errors.candidates')))
      })
    return () => {
      cancelled = true
    }
  }, [tx.id, reloadKey, t])

  // Lo que queda del movimiento por aplicar. Un cobro puede pagar VARIAS
  // facturas: se aplica a cada una lo que deba, no el importe entero.
  const remaining = Math.abs(tx.amountCents) - tx.appliedCents

  /**
   * Qué hacer después de aplicar algo: si el movimiento ya está entero, cerrar;
   * si no, quedarse abierto diciendo cuánto falta y por dónde seguir.
   */
  const afterApply = async (updated: BankTransactionDto) => {
    await onRefresh()
    const left = Math.abs(updated.amountCents) - updated.appliedCents
    if (left <= 0) {
      await onDone()
      return
    }
    setTx(updated)
    setCandidates(null)
    setReloadKey((k) => k + 1)
    setNotice(t('reconcile.partialNotice', { amount: formatCents(left) }))
  }

  const [closing, setClosing] = useState(false)
  /**
   * Liquidación de socio: cuando alguien adelantó gastos de su bolsillo, la
   * transferencia con la que se le devuelve el dinero no casa con ninguna
   * factura ni gasto —esos ya están pagados—, sino con su cuenta corriente
   * (551). Una sola transferencia puede saldar varios gastos a la vez.
   */
  const [partners, setPartners] = useState<
    { userId: string; name: string; owedCents: number }[]
  >([])
  const [settling, setSettling] = useState<string | null>(null)
  /**
   * Socio cuya liquidación se está preparando, y sus adelantos pendientes con
   * lo que se ha marcado de cada uno. Jesús eligió marcar a mano qué cubre la
   * transferencia en vez de repartir por antigüedad: así el reembolso dice
   * exactamente lo que se ha devuelto.
   */
  const [settleFor, setSettleFor] = useState<{ userId: string; name: string } | null>(null)
  const [advances, setAdvances] = useState<PartnerAdvanceDto[]>([])
  const [picked, setPicked] = useState<Record<string, boolean>>({})

  const openSettle = async (userId: string, name: string) => {
    setError(null)
    setSettleFor({ userId, name })
    try {
      const r = await api.get<PartnerAdvancesResponse>(
        `/banking/partner-advances?userId=${userId}`
      )
      setAdvances(r.items)
      // Se premarca lo que cabe en el movimiento, del más antiguo al más
      // reciente: el caso normal es "esta transferencia salda lo más viejo".
      const pre: Record<string, boolean> = {}
      let left = remaining
      for (const a of r.items) {
        if (a.pendingCents <= left) {
          pre[a.expensePayerId] = true
          left -= a.pendingCents
        }
      }
      setPicked(pre)
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
      setAdvances([])
    }
  }

  const pickedTotal = advances
    .filter((a) => picked[a.expensePayerId])
    .reduce((x, a) => x + a.pendingCents, 0)

  useEffect(() => {
    // Solo tiene sentido en salidas de dinero y mientras quede algo por aplicar.
    if (tx.amountCents >= 0) return
    api
      .get<{ items: { userId: string; name: string; owedCents: number }[] }>(
        '/banking/partner-balances'
      )
      .then((r) => setPartners(r.items.filter((p) => p.owedCents > 0)))
      .catch(() => setPartners([]))
    // `reloadKey` se sube al aplicar algo: si acabamos de saldar a un socio, lo
    // que se le debe ha cambiado y la lista tiene que reflejarlo sin cerrar.
  }, [tx.amountCents, reloadKey])

  const settlePartner = async (userId: string, owedCents: number) => {
    setSettling(userId)
    setError(null)
    try {
      // Topado por los dos lados, igual que al conciliar una factura: ni más de
      // lo que se le debe, ni más de lo que queda en el movimiento.
      const allocations = advances
        .filter((a) => picked[a.expensePayerId])
        .map((a) => ({ expensePayerId: a.expensePayerId, amountCents: a.pendingCents }))
      const r = await api.post<{ transaction: BankTransactionDto; entryError: string | null }>(
        `/banking/transactions/${tx.id}/settle-partner`,
        {
          userId,
          amountCents: allocations.length > 0 ? pickedTotal : Math.min(remaining, owedCents),
          ...(allocations.length > 0 ? { allocations } : {}),
        }
      )
      if (r.entryError) toast.error(t('reconcile.entryFailed', { error: r.entryError }))
      setSettleFor(null)
      await afterApply(r.transaction)
    } catch (err) {
      setError(errorText(err, t('errors.reconcile')))
    } finally {
      setSettling(null)
    }
  }

  /**
   * Da por cerrado el sobrante. Lo lleva a resultados con su asiento, porque ese
   * dinero está en el banco: marcarlo conciliado sin contrapartida dejaría la
   * 572 descuadrada contra el extracto.
   */
  const closeRemainder = async () => {
    setClosing(true)
    setError(null)
    try {
      const r = await api.post<{ entryError: string | null }>(
        `/banking/transactions/${tx.id}/close-remainder`,
        {}
      )
      if (r.entryError) toast.error(t('reconcile.entryFailed', { error: r.entryError }))
      await onDone()
    } catch (err) {
      setError(errorText(err, t('errors.reconcile')))
    } finally {
      setClosing(false)
    }
  }

  const reconcile = async (c: MatchCandidateDto) => {
    // Topado por los dos lados: ni más de lo que debe la factura, ni más de
    // lo que queda en el movimiento.
    const amountCents = Math.min(remaining, c.pendingCents)
    // Aplicar MENOS de lo que el documento debe es legítimo (un cobro que paga
    // varias facturas) pero también es la forma de meter la pata sin remedio:
    // no existe deshacer conciliación, y un gasto conciliado ya no se puede ni
    // anular ni reasignar. Si no cubre el documento entero, se pregunta.
    if (amountCents < c.pendingCents) {
      const ok = await confirmDialog({
        title: t('reconcile.confirmPartialTitle'),
        message: t('reconcile.confirmPartialBody', {
          amount: formatCents(amountCents),
          pending: formatCents(c.pendingCents),
          party: c.partyName,
        }),
      })
      if (!ok) return
    }
    setBusyId(c.id)
    setError(null)
    try {
      const r = await api.post<{ transaction: BankTransactionDto; entryError: string | null }>(
        `/banking/transactions/${tx.id}/reconcile`,
        { kind: c.kind, targetId: c.id, amountCents }
      )
      if (r.entryError) {
        // El cobro se hizo (el dinero se movió de verdad); el asiento no. Se
        // dice, no se calla.
        toast.error(t('reconcile.entryFailed', { error: r.entryError }))
      }
      await afterApply(r.transaction)
    } catch (err) {
      setError(errorText(err, t('errors.reconcile')))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('reconcile.title')} maxWidth="max-w-2xl">
      <div className="space-y-3">
        <div className="rounded-lg border border-base p-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="t-primary">{tx.concept}</span>
            <span
              className={[
                'font-mono font-semibold whitespace-nowrap',
                tx.amountCents < 0
                  ? 'text-rose-700 dark:text-rose-300'
                  : 'text-emerald-700 dark:text-emerald-300',
              ].join(' ')}
            >
              {formatCents(tx.amountCents)}
            </span>
          </div>
          <div className="text-[11px] t-faint mt-0.5">
            {new Date(tx.operationDate).toLocaleDateString(currentLocaleTag())}
            {tx.reference && ` · ${tx.reference}`}
          </div>
          {notice && (
            <p className="mt-2 text-xs rounded-lg p-2 text-amber-800 dark:text-amber-200 bg-amber-500/10 border border-amber-500/30">
              {notice}
            </p>
          )}
          {tx.appliedCents > 0 && (
            <div className="mt-2 space-y-1.5">
              <div className="text-[11px] flex items-center gap-2 flex-wrap">
                <span className="t-muted">
                  {t('reconcile.applied', { amount: formatCents(tx.appliedCents) })}
                </span>
                <span className="font-mono text-amber-700 dark:text-amber-300">
                  {t('reconcile.remaining', { amount: formatCents(remaining) })}
                </span>
              </div>
              {/* Qué ha pagado ya, una línea por aplicación. */}
              {tx.applications.length > 0 && (
                <ul className="text-[11px] t-faint space-y-0.5">
                  {tx.applications.map((a, i) => (
                    <li key={`${a.targetId ?? a.kind}-${i}`} className="flex justify-between gap-2">
                      <span>· {a.label ?? t('reconcile.writeoff')}</span>
                      <span className="font-mono">{formatCents(a.amountCents)}</span>
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                disabled={closing}
                onClick={closeRemainder}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] border border-base t-secondary hover:bg-hover disabled:opacity-50"
              >
                {closing ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                {t('reconcile.closeRemainder', { amount: formatCents(remaining) })}
              </button>
            </div>
          )}
        </div>

        {error && (
          <p className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <AlertCircle size={12} /> {error}
          </p>
        )}

        {!candidates ? (
          <div className="t-faint text-sm flex items-center gap-1.5 py-6 justify-center">
            <Loader2 size={14} className="animate-spin" /> {t('loading')}
          </div>
        ) : candidates.length === 0 ? (
          <p className="text-sm t-muted py-4 text-center">{t('reconcile.noCandidates')}</p>
        ) : (
          <ul className="space-y-2">
            {candidates.map((c) => {
              // Lo que se va a aplicar NO es el pendiente del documento, sino lo
              // que quede del movimiento si es menor. La ficha enseñaba el
              // pendiente y el botón decía solo «Conciliar»: con 0,01 € por
              // aplicar y una factura de 320 € en la lista, un clic registraba
              // un pago de UN CÉNTIMO contra esa factura. Y no hay deshacer
              // conciliación: el gasto se queda además sin poder anularse.
              const willApply = Math.min(remaining, c.pendingCents)
              const partial = willApply < c.pendingCents
              return (
              <li key={`${c.kind}-${c.id}`} className="rounded-lg border border-base p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm t-primary truncate">
                      <span className="font-mono text-xs t-muted mr-2">{c.number || '—'}</span>
                      {c.partyName}
                    </div>
                    <ul className="text-[11px] t-faint mt-1 space-y-0.5">
                      {c.reasons.map((r) => (
                        <li key={r}>· {r}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm t-primary">{formatCents(c.pendingCents)}</div>
                    <div className="text-[10px] t-faint">
                      {new Date(c.date).toLocaleDateString(currentLocaleTag())}
                    </div>
                    {partial && (
                      <div className="text-[10px] font-mono text-amber-700 dark:text-amber-300 mt-0.5">
                        {t('reconcile.willApply', { amount: formatCents(willApply) })}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => void reconcile(c)}
                      disabled={busyId !== null}
                      className="mt-2 px-2.5 py-1 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-xs disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {busyId === c.id ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <Check size={11} />
                      )}
                      {partial
                        ? t('reconcile.confirmAmount', { amount: formatCents(willApply) })
                        : t('reconcile.confirm')}
                    </button>
                  </div>
                </div>
              </li>
              )
            })}
          </ul>
        )}

          {/* ☠️ FUERA del bloque «hay algo aplicado». Una transferencia de
              reembolso a un socio no casa con ninguna factura —esos gastos ya
              están pagados— y llega con appliedCents = 0, así que este bloque
              no se pintaba NUNCA en su caso principal y la liquidación era
              inalcanzable desde la pantalla. «Cerrar el resto» sí se queda
              dentro: el servidor lo rechaza sin nada aplicado. */}
          {remaining > 0 && (
            <div className="mt-2 space-y-1.5">
              {partners.length > 0 && (
                <div className="pt-1 space-y-1">
                  <p className="text-[11px] t-muted">{t('reconcile.settlePartnerTitle')}</p>
                  {partners.map((p) => (
                    <button
                      key={p.userId}
                      type="button"
                      disabled={settling !== null}
                      onClick={() => void openSettle(p.userId, p.name)}
                      className="w-full inline-flex items-center justify-between gap-2 px-2 py-1 rounded-md text-[11px] border border-base t-secondary hover:bg-hover disabled:opacity-50"
                    >
                      <span className="flex items-center gap-1.5">
                        {settling === p.userId ? (
                          <Loader2 size={11} className="animate-spin" />
                        ) : (
                          <Check size={11} />
                        )}
                        {t('reconcile.settlePartner', { name: p.name })}
                      </span>
                      <span className="font-mono">{formatCents(Math.min(remaining, p.owedCents))}</span>
                    </button>
                  ))}
                </div>
              )}
              {settleFor && (
                <div className="mt-1 border border-base/60 rounded-lg p-2 space-y-1.5">
                  <p className="text-[11px] t-muted">
                    {t('reconcile.pickAdvances', { name: settleFor.name })}
                  </p>
                  {advances.length === 0 ? (
                    <p className="text-[11px] t-faint">{t('reconcile.noAdvances')}</p>
                  ) : (
                    advances.map((a) => (
                      <label
                        key={a.expensePayerId}
                        className="flex items-center gap-2 text-[11px] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={!!picked[a.expensePayerId]}
                          onChange={(e) =>
                            setPicked((prev) => ({
                              ...prev,
                              [a.expensePayerId]: e.target.checked,
                            }))
                          }
                          className="rounded accent-emerald-500"
                        />
                        <span className="font-mono t-faint">{a.code}</span>
                        <span className="truncate flex-1 t-secondary">{a.supplierName}</span>
                        <span className="font-mono">{formatCents(a.pendingCents)}</span>
                      </label>
                    ))
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span
                      className={
                        pickedTotal > remaining ? 'text-[11px] text-rose-500' : 'text-[11px] t-faint'
                      }
                    >
                      {formatCents(pickedTotal)} / {formatCents(remaining)}
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSettleFor(null)}
                        className="px-2 py-1 rounded-md text-[11px] border border-base t-secondary hover:bg-hover"
                      >
                        {t('common:cancel')}
                      </button>
                      <button
                        type="button"
                        disabled={settling !== null || pickedTotal <= 0 || pickedTotal > remaining}
                        onClick={() => void settlePartner(settleFor.userId, pickedTotal)}
                        className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {settling && <Loader2 size={11} className="animate-spin" />}
                        {t('reconcile.confirmSettle', { amount: formatCents(pickedTotal) })}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
    </Modal>
  )
}


/**
 * Conecta la cuenta con el banco (PSD2). Pide al backend la URL de
 * autorización y manda al usuario a la web del BANCO: las credenciales de
 * banca online no pasan por el CRM en ningún momento.
 *
 * Si ya está conectada, el botón pasa a sincronizar y muestra cuándo caduca
 * el consentimiento, que hay que renovar (Sabadell admite hasta 180 días).
 */
function ConnectBankButton({
  account,
  onError,
}: {
  account: BankAccountDto
  onError: (msg: string | null) => void
}) {
  const { t } = useTranslation('banking')
  const [busy, setBusy] = useState(false)
  const connected = account.feed === 'ENABLE_BANKING'

  const run = async () => {
    setBusy(true)
    onError(null)
    try {
      if (connected) {
        const r = await api.post<{ imported: number; duplicates: number }>(
          `/banking/accounts/${account.id}/sync`,
          {}
        )
        toast.success(t('feed.synced', { count: r.imported }))
        window.location.reload()
      } else {
        // Se recuerda la cuenta ANTES de salir: el proveedor no garantiza
        // devolvernos `state`, y sin esto el callback no sabría a qué cuenta
        // enganchar la sesión.
        sessionStorage.setItem(PENDING_KEY, account.id)
        // Y se manda el origen desde el que se navega, para volver al MISMO
        // dominio (crm.jr2.es o jr2crmos.jr2.es) en vez de saltar al otro y
        // pedir sesión de nuevo. El backend solo lo acepta si está registrado.
        const { url } = await api.post<{ url: string }>(
          `/banking/accounts/${account.id}/connect`,
          { redirectOrigin: window.location.origin }
        )
        // Salimos a la web del banco; volverá a /banking/callback.
        window.location.href = url
      }
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      onError(body?.error ?? (err instanceof Error ? err.message : t('errors.generic')))
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => void run()}
      disabled={busy}
      title={
        connected && account.feedValidUntil
          ? t('feed.validUntil', {
              date: new Date(account.feedValidUntil).toLocaleDateString(currentLocaleTag()),
            })
          : undefined
      }
      className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50 flex items-center gap-1.5"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={14} />}
      {connected ? t('feed.sync') : t('feed.connect')}
    </button>
  )
}

/**
 * Aviso de caducidad del consentimiento PSD2 en la propia tarjeta de la cuenta.
 *
 * La notificación de la campana avisa a los ADMIN, pero quien mira Tesorería
 * quiere verlo aquí, sin abrir nada. Solo aparece cuando falta poco (o ya
 * caducó): mostrar "faltan 174 días" todos los días es ruido que se aprende a
 * ignorar, y entonces tampoco se ve el aviso que importa.
 */
const CONSENT_NOTICE_DAYS = 30

/**
 * El saldo de la tarjeta.
 *
 * El número que da el banco envejece: el cupo PSD2 (4 accesos diarios sin el
 * titular delante) obliga a que el tick automático traiga movimientos pero NO
 * saldo, así que entre lectura y lectura pueden entrar apuntes que ese saldo no
 * incluye. Cuando el backend puede deducir el saldo al día con certeza
 * (`computedBalanceCents`, ver lib/banking/current-balance.ts) se enseña ése
 * como cifra principal y debajo de dónde sale; cuando no, se enseña el del banco
 * y se avisa de cuántos movimientos han entrado después.
 *
 * La hora va siempre: un saldo de las 00:04 con solo la fecha de hoy parece
 * actual, y ése fue justo el desconcierto del 05/08/2026.
 */
function BalanceBlock({ account: a }: { account: BankAccountDto }) {
  const { t } = useTranslation('banking')
  const stamp = a.lastBalanceAt
    ? new Date(a.lastBalanceAt).toLocaleString(currentLocaleTag(), {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null
  const bank = a.lastBalanceCents
  const computed = a.computedBalanceCents
  // Solo es novedad si el deducido difiere del que dijo el banco.
  const isAhead = computed != null && bank != null && computed !== bank
  const headline = isAhead ? computed : bank

  return (
    <>
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider t-muted">
          {isAhead ? t('currentBalance') : t('lastBalance')}
        </span>
        <span className="font-mono text-sm t-primary">
          {headline != null ? formatCents(headline) : '—'}
        </span>
      </div>
      {isAhead ? (
        <div className="text-[10px] t-faint text-right leading-tight">
          {t('balanceFromFeed', {
            bank: formatCents(bank!),
            stamp,
            count: a.balanceDeltaCount,
          })}
        </div>
      ) : (
        <>
          <div className="text-[10px] t-faint text-right">{stamp ?? t('neverImported')}</div>
          {a.balanceDeltaCount > 0 && (
            <div className="text-[10px] text-amber-700 dark:text-amber-300 text-right leading-tight">
              {t('balanceStale', {
                count: a.balanceDeltaCount,
                amount: formatCents(a.balanceDeltaCents),
              })}
            </div>
          )}
        </>
      )}
    </>
  )
}

function ConsentNotice({ account }: { account: BankAccountDto }) {
  const { t } = useTranslation('banking')
  if (account.feed !== 'ENABLE_BANKING' || !account.feedValidUntil) return null

  const validUntil = new Date(account.feedValidUntil)
  if (Number.isNaN(validUntil.getTime())) return null
  const msLeft = validUntil.getTime() - Date.now()
  const expired = msLeft <= 0
  // Hacia arriba, igual que el aviso del backend: con 6 días y medio quedan 7.
  const daysLeft = Math.ceil(msLeft / (24 * 3600 * 1000))
  if (!expired && daysLeft > CONSENT_NOTICE_DAYS) return null

  return (
    <div
      className={[
        'mt-2 flex items-center gap-1.5 text-[10px] rounded-md border px-1.5 py-1',
        expired
          ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40'
          : 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
      ].join(' ')}
    >
      <AlertCircle size={12} className="shrink-0" />
      <span>
        {expired
          ? t('feed.consentExpired')
          : t('feed.consentExpiring', {
              count: daysLeft,
              date: validUntil.toLocaleDateString(currentLocaleTag()),
            })}
      </span>
    </div>
  )
}


/**
 * El último fallo del banco, en pantalla.
 *
 * Antes la interfaz decía "se ha registrado el detalle en el servidor" y ahí
 * moría: para saber qué pasaba había que entrar por SSH a mirar logs. El texto
 * ya estaba guardado en la cuenta (`feedLastError`), solo que no se enseñaba.
 */
function FeedErrorModal({
  account,
  onClose,
}: {
  account: BankAccountDto
  onClose: () => void
}) {
  const { t } = useTranslation('banking')
  const raw = account.feedLastError ?? ''
  // El 429 del banco es el caso frecuente y tiene una explicación concreta: no
  // hay nada roto, se ha agotado el cupo diario de accesos que fija PSD2.
  const rateLimited = raw.includes('429')

  return (
    <Modal open onClose={onClose} title={t('feed.errorTitle')} maxWidth="max-w-xl">
      <div className="space-y-3">
        {rateLimited && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200">
            {t('feed.rateLimitedHelp')}
          </div>
        )}
        <div>
          <div className="text-[11px] uppercase tracking-wider t-muted mb-1">
            {t('feed.lastAttempt')}
          </div>
          <div className="text-sm t-secondary">
            {account.feedLastSyncAt
              ? new Date(account.feedLastSyncAt).toLocaleString(currentLocaleTag())
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider t-muted mb-1">
            {t('feed.errorDetail')}
          </div>
          <pre className="text-[11px] font-mono whitespace-pre-wrap break-all rounded-lg border border-base p-3 bg-[var(--bg-input)] t-secondary max-h-64 overflow-auto">
            {raw || '—'}
          </pre>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('close', { ns: 'common' })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
